import GroupList from 'components/GroupList'
import React, { useState, useRef, useEffect, useCallback } from 'react'
import _ from 'lodash';
import { assignParticipants, cloneData, getResultData } from 'lib/helper';
import AdjacentMatrixViewInternal from 'components/AdjacentMatrix/AdjacentMatrixInternal';
import GaugeChart from 'components/Gauge';
import html2canvas from 'html2canvas';
import ResizeObserver from 'resize-observer-polyfill';
import ResultInternal from 'components/ResultInternal';
import MyTour from './tour';
import { loadFont } from 'lib/helper';

const matrixFilter = {
  axis: ["groupName", "title", "name"].reverse(),
  axisText: ["Department", "Job title", "Name"].reverse(),
};
const profile = {};


function testDownload(canvas) {
  const url = canvas.toDataURL();
  const a = document.createElement('a');
  a.download = 'file.png';
  a.href = url;
  a.click();
}

function DesignCoordination({ designData, rect }) {
  let coordLoadInPercent;
  let coordLoad = 0;
  let fitness;

  if (designData?.result) {
    const { base_fitness, result, optimize_type, participants } = designData

    let optKey = 'Fitness-' + ['Lean', 'Balanced', 'Independent'][optimize_type - 1]
    fitness = result[optKey]
    if (!fitness) fitness = result.Fitness
    coordLoadInPercent = (fitness / base_fitness[optKey] - 1) * 100
    if (coordLoadInPercent > 100) {
      coordLoadInPercent = 100
    } else if (coordLoadInPercent < -100) {
      coordLoadInPercent = -100
    }
    coordLoad = fitness / participants.data.length
    window.coordLoadInPercent = coordLoadInPercent;
  }

  const scale = rect ? Math.min(rect.width / 250, 1.3) : 1;
  const width = scale * 250;
  const height = scale * 200;
  const top = rect ? (rect.height - height) / 2 : 0;
  const left = rect ? (rect.width - width) / 2 : 0;

  return (
    <div className="coord-load" style={{
      transform: `scale(${scale})`,
      top: `${top}px`,
      left: `${left}px`,
    }}>
      <div className="text-center title"> Coordination Load: {coordLoad.toFixed(2)}<br /> (lower is more efficient) </div>
      <GaugeChart value={coordLoadInPercent || 0}>
        Vs the starting organization
      </GaugeChart>
    </div>
  )
}

const limits = [
  {
    name: 'Fitness-Lean',
    alpha: 0.2,
    beta: 0.6,
  },
  {
    name: 'Fitness-Balanced',
    alpha: 0.4,
    beta: 0.3,
  },
  {
    name: 'Fitness-Independent',
    alpha: 0.6,
    beta: 0.2,
  }
]

function lg2(x) {
  return Math.log(x) / Math.log(2);
}

function getFitness(nodes, links) {
  const groups = Object.values(_.groupBy(nodes, node => node.group));
  const n = nodes.length;
  const linkmap = _.mapValues(_.groupBy(links, link => link.source.id),
    (linkList => {
      return linkList.reduce((acc, curr) => {
        acc[curr.target.id] = 1
        return acc;
      }, {})
    }));
  let s1 = 0, s2 = 0;
  for (let i = 0; i < n; i++) for (let j = 0; j < n; j++) {
    if (i === j) {
      continue;
    }
    const ii = nodes[i].id;
    const jj = nodes[j].id;
    const linked = linkmap[ii] && linkmap[ii][jj];

    if (ii !== jj && linked && nodes[i].group !== nodes[j].group) {
      s1++;
    }
    if (ii !== jj && !linked && nodes[i].group === nodes[j].group) {
      s2++;
    }
  }
  const result = {};
  limits.forEach(limit => {
    const alpha = limit.alpha;
    const beta = limit.beta;
    const mdlWeight = 1 - alpha - beta;
    const mdl = (groups.length + n) * lg2(n);
    const typeErrorScore = alpha * s1 * 2 * lg2(n + 1) + beta * s2 * 2 * lg2(n + 1);
    const fitness = mdlWeight * mdl + typeErrorScore;
    result[limit.name] = fitness;
  });
  return result;
}

function addResult(result, newRow) {
  const index = result.findIndex(r => r.email === newRow.email);
  if (index > -1) {
    result.splice(index, 1);
  }
  const newResult = [...result, newRow];
  newResult.sort((a, b) => {
    if (a.load !== b.load) {
      return a.load - b.load;
    } else {
      if (a.email === newRow.email) {
        return -1;
      } else {
        if (a.id && b.id) {
          return a.id - b.id;
        }
      }
    }
  });
  const rank = newResult.findIndex(r => r.email === newRow.email);
  newRow = {
    ...newRow,
    rank,
  }
  return [...newResult.slice(0, 4), newRow];
}

export default function Demo() {
  const [allData, setAllData] = useState(null);
  const [data, setData] = useState(null);
  const groupListRef = useRef();
  const matrixRef = useRef();
  const [choosen, setChoosen] = useState(null);
  const [result, setResult] = useState(null);
  const [currentEmail, setCurrentEmail] = useState(null);
  const [jsonId, setJsonId] = useState(0);
  const [contentWidth, setContentWidth] = useState(null);
  const [counts, setCounts] = useState([0, 0]);
  const [type, setType] = useState('demo');
  const [tourSync, setTourSync] = useState(0);

  useEffect(() => {
    fetch(`/api/`, {
      method: 'GET',
    }).then((response) => response.json()).then((json) => {
      setAllData(json);
      setCounts([json.demo.result.nodes.length, json.challenge.result.nodes.length]);
      const demoJson = json.demo;
      assignParticipants(demoJson);
      setData(demoJson);
      setJsonId(demoJson.id);
      setType('demo');
    });

    const observer = new ResizeObserver(() => {
      const bounds = document.body.getBoundingClientRect();
      const msg = {
        type: 'set-bound',
        target: 'demo',
        bounds,
      };
      window.parent.postMessage(msg, "*");
      setContentWidth(document.body.clientWidth);
    });

    observer.observe(document.body);

    return () => {
      observer.disconnect();
    }

  }, [])

  const [coordRect, setCoordRect] = useState(null);

  useEffect(() => {
    let rectInterval = null;
    const matrixObserver = new ResizeObserver(() => {
      const matrixBackgroundRect = document.querySelector('.matrixview .matrix-corner');
      if (matrixBackgroundRect) {
        const rect = matrixBackgroundRect.getBoundingClientRect();
        setCoordRect(rect);
      } else {
        clearInterval(rectInterval);
        rectInterval = setInterval(() => {
          const matrixBackgroundRect = document.querySelector('.matrixview .matrix-corner');
          if (matrixBackgroundRect) {
            const rect = matrixBackgroundRect.getBoundingClientRect();
            setCoordRect(rect);
            clearInterval(rectInterval);
          }
        }, 100);
      }
    });
    const interval = setInterval(() => {
      const el = document.querySelector('.matrix-view');
      if (el) {
        matrixObserver.observe(el);
        clearInterval(interval);
      }
    }, 100);

    return () => {
      matrixObserver.disconnect();
    }
  }, [matrixRef.current])

  const saveDesign = (newData) => {
    const { valid, nodes, allAssigned, group_mapping } = getResultData(newData, data.participants.data);
    if (valid) {
      const links = data.result.links;
      links.forEach(link => {
        link.sourceId = link.source.id
        link.source = nodes.find(node => node.id === link.sourceId)
        link.targetId = link.target.id
        link.target = nodes.find(node => node.id === link.targetId)
      })
      setData({
        ...data,
        result: {
          ...data.result,
          nodes,
          links,
          ...getFitness(nodes, data.result.links),
        },
        group_mapping
      });
    }
  };

  const handleGroupsChanged = useCallback(() => {
    setTourSync(x => x + 1);
  }, []);

  const [start_time, setStart_time] = useState(null);

  const submitResult = (firstName, lastName, jobTitle, organization, email) => {
    if (window.coordLoadInPercent !== undefined) {
      window.email = email;
      setCurrentEmail(email);
      fetch(`/api/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user: firstName + ' ' + lastName,
          jobTitle,
          organization,
          email,
          load: window.coordLoadInPercent,
          id: jsonId,
          start_time,
          end_time: new Date(),
        }),
      }).then(() => {
        window.parent.postMessage({
          type: 'show-result',
          email: email,
          id: jsonId,
          resultType: type,
        }, "*");
        fetch(`/api/result/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: email,
            id: jsonId,
          }),
        }).then(response => response.json()).then((res) => {
          setResult(res);
          window.sendEmail = () => {
            const ele = document.getElementsByClassName('demoshow')[0].cloneNode(true);
            document.body.appendChild(ele);
            ele.classList.add('cloned');
            loadFont(async (css) => {
              const style = document.createElement('style');
              style.innerHTML = css;
              ele.querySelector('.cloned .matrixview svg').prepend(style);
              const canvas1 = await html2canvas(ele.querySelector('.cloned .democontent'), {
                onclone: function (doc) {
                  const matrixWrapper = doc.querySelector('.cloned .matrixview');
                  const bounds = matrixWrapper.getBoundingClientRect();
                  const svg = doc.querySelector('.cloned .matrixview svg');
                  svg.setAttribute('width', bounds.width);
                  svg.setAttribute('height', bounds.width);
                  svg.style.width = `${bounds.width}px !important`;
                  svg.style.height = `${bounds.width}px !important`;
                }
              });
              const canvas2 = await html2canvas(ele.querySelector('.result'));

              // testDownload(canvas1);
              // testDownload(canvas2);

              const [imgMain, imgResult] = await Promise.all([
                new Promise((resolve) => canvas1.toBlob((blob) => resolve(blob))),
                new Promise((resolve) => canvas2.toBlob((blob) => resolve(blob))),
              ]);
              const formData = new FormData();
              formData.append('user', firstName + ' ' + lastName)
              formData.append('email', email);
              formData.append('imgMain', imgMain);
              formData.append('imgResult', imgResult)
              formData.append('id', jsonId);

              fetch(`/api/email/`, {
                method: 'POST',
                body: formData,
              });
            })
          };
        });
      });
    }
  }


  window.submitResult = submitResult;

  const startChallenge = () => {
    const challengeJson = allData.challenge;
    setStart_time(new Date());
    fetch(`/api/count/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: challengeJson.id,
      }),
    });
    if (type !== 'challenge') {
      setData(null);
      setTimeout(() => {
        const cp = cloneData(challengeJson)
        assignParticipants(cp);
        setData(cp);
        setJsonId(cp.id);
        setType('challenge');
      }, 100);
    }
  }

  const startDemo = () => {
    const demoJson = allData.demo;
    setStart_time(new Date());
    fetch(`/api/count/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: demoJson.id,
      }),
    });
    if (type !== 'demo') {
      setData(null);
      setTimeout(() => {
        const cp = cloneData(demoJson)
        assignParticipants(cp);
        setData(cp);
        setJsonId(cp.id);
        setType('challenge');
      }, 100);
    }
  }

  window.startChallenge = startChallenge;

  const gridWidth = contentWidth ? Math.floor(contentWidth / 2 / 252) * 252 : undefined;

  const width = gridWidth ? `${gridWidth}px` : 'auto';

  let coordLoadInPercent;
  let coordLoad;

  if (data) {
    const { base_fitness, optimize_type, participants } = data;
    let optKey = 'Fitness-' + ['Lean', 'Balanced', 'Independent'][optimize_type - 1];
    const fitness = data.score;
    coordLoadInPercent = (fitness / base_fitness[optKey] - 1) * 100;
    coordLoad = fitness / participants.data.length;
  }

  return (
    <div className="demoshow">
      { data && <h2>The Reconfig AI was able to achieve a coordination load of {coordLoad.toFixed(2)} which is an {`${Math.abs(coordLoadInPercent).toFixed(0)}%`} improvement</h2>}
      {counts && <MyTour startDemo={startDemo} startChallenge={startChallenge} counts={counts} tourSync={tourSync} />}
      <div className="democontent" id="democontent">
        <div className="groupedit" style={{
          width,
        }}>
          {data && <GroupList ref={groupListRef} data={data} labels={['name', 'title']} save={saveDesign} hideTitle={true} demo={type} setChoosen={setChoosen} onGroupsChanged={handleGroupsChanged} />}
        </div>
        <div className="matrix-view" ref={matrixRef}>
          {data && (
            <>
              <DesignCoordination designData={data} rect={coordRect} />
              <AdjacentMatrixViewInternal
                data={data}
                hideFilter={true}
                matrixFilter={matrixFilter}
                activityMap={data.activityMap}
                profile={profile}
                notCollapse={true}
                width={250}
                height={250}
                setMatrixFilter={null}
                choosen={choosen}
              />
            </>
          )}
        </div>
      </div>
      <div className="result-wrapper">
        {data && <ResultInternal result={result} currentEmail={currentEmail} />}
      </div>
      <div className='bottom'></div>
    </div>
  )
}
